import { Bell, BellSlash, ClipboardText, Files, Gear, PencilSimple, PushPin, PushPinSlash, Trash, UserCircle } from '@phosphor-icons/react'
import { ThreadWithMetadata } from 'ClientPortal/src/api/Queries/threads'
import { IconButtonWithOptions } from 'ComponentLibrary/src/piazza'

import { useLocation, useNavigate } from 'react-router-dom'
import type { AppraiserUser, CustomerUser } from '@einwert/valuation-api-client'
import { useTranslation } from 'react-i18next'
import { getUserSettingsModalUrl } from 'ComponentLibrary'
import { getAppraiserUIBaseUrl } from 'ClientPortal/src/common/environment'

type MessageListHeaderOptionsMenuProps = {
    thread: ThreadWithMetadata
    isPinned: boolean
    currentUser?: AppraiserUser | CustomerUser
    isPatchingThread: boolean
    pinThread: (threadId: string) => void
    unpinThread: (threadId: string) => void
    onReplaceParticipantIds: (participantIds: number[]) => void
    onEditParticipants: (isEditingParticipants: boolean) => void
    onEditTitle?: (isEditingParticipants: boolean) => void
    showTaskAndOrderNavigations?: boolean
    deleteThread?: (threadId: string) => void
}

const MessageListHeaderOptionsMenu = ({
    thread,
    isPinned,
    currentUser,
    onReplaceParticipantIds,
    pinThread,
    unpinThread,
    onEditParticipants,
    onEditTitle,
    showTaskAndOrderNavigations,
    deleteThread,
}: MessageListHeaderOptionsMenuProps) => {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.threadListPage.menu' })
    const { t: tThreadListPage } = useTranslation('pages', { keyPrefix: 'orders.threadListPage' })
    const addCurrentUserToParticipants = () => currentUser && onReplaceParticipantIds([...thread.participant_ids, currentUser.id])

    const navigate = useNavigate()
    const location = useLocation()

    const removeCurrentUserFromParticipants = () =>
        currentUser && onReplaceParticipantIds(thread.participant_ids.filter((id) => id !== currentUser.id))

    const navigateToNotificationSettings = () => {
        if (currentUser?.role === 'appraiser') {
            window.open(`${getAppraiserUIBaseUrl()}/orders?settings=notifications`, '_blank')
            return
        }
        void navigate(
            getUserSettingsModalUrl({
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
                tab: 'notifications',
            })
        )
    }

    const isNotifiedForCurrentThread = currentUser && thread.participant_ids.includes(currentUser.id)
    return (
        <IconButtonWithOptions
            variant="tertiary"
            size="small"
            tooltip={tThreadListPage('options')}
            options={[
                isPinned && {
                    title: t('unpin'),
                    icon: <PushPinSlash />,
                    callback: () => unpinThread(thread.uuid),
                },
                !isPinned && {
                    title: t('pin'),
                    icon: <PushPin />,
                    callback: () => pinThread(thread.uuid),
                },
                onEditTitle &&
                    !thread.associatedTask && {
                        icon: <PencilSimple />,
                        title: t('rename'),
                        callback: () => onEditTitle(true),
                    },
                showTaskAndOrderNavigations &&
                    thread.associatedTask && {
                        icon: <ClipboardText />,
                        title: t('goToTask'),
                        callback: () =>
                            thread.associatedTask && navigate(`/orders/${thread.order_uuid}/tasks?task=${thread.associatedTask.uuid}`),
                    },
                showTaskAndOrderNavigations && {
                    icon: <Files />,
                    title: t('goToOrder'),
                    callback: () => navigate(`/orders/${thread.order_uuid}/overview`),
                },
                {
                    icon: <UserCircle />,
                    title: t('editParticipants'),
                    callback: () => onEditParticipants(true),
                },
                isNotifiedForCurrentThread && {
                    icon: <BellSlash />,
                    title: t('stopNotifications'),
                    callback: removeCurrentUserFromParticipants,
                },
                !isNotifiedForCurrentThread && {
                    icon: <Bell />,
                    title: t('notifyMe'),
                    callback: addCurrentUserToParticipants,
                },
                deleteThread &&
                    !thread.associatedTask && {
                        title: t('delete'),
                        icon: <Trash />,
                        callback: () => deleteThread(thread.uuid),
                        danger: true,
                    },
                {
                    icon: <Gear />,
                    title: t('notificationSettings'),
                    callback: navigateToNotificationSettings,
                },
            ].filter((item) => !!item)}
        />
    )
}

export default MessageListHeaderOptionsMenu
